/**
 * EmergencyInterventionDialog.jsx — names the emergency intervention for a card.
 *
 * What: A single required free-text input that captures the clinical intervention
 *       name (e.g. "Cardioversion", "Rapid sequence intubation") when a clinician
 *       flags a patient as an emergency intervention.
 * Why:  The red card alone signals emergency status; the intervention NAME is the
 *       clinically useful information. There is no "Emergency Intervention" label
 *       anywhere; only the name is recorded/shown.
 * Invariants:
 *   - Save is disabled until a non-empty name is entered (the flag is always
 *     intentional and always named — never auto-set silently).
 *   - Used both for the manual "Flag emergency intervention" action and for the
 *     ESI 1 prompt; the copy adapts to whichever entry point opened it.
 *
 * Ported from HealthDesk Experimental (Exp-B) — no adaptation.
 * Tightest coupling: SavoirCareBoardPage.jsx (owns the single instance),
 *   useSavoirCareBoard.flagEmergencyIntervention (persists the result).
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Alert,
} from '@mui/material';

/** Max length of the intervention name (matches the hook + Firestore expectations). */
const INTERVENTION_NAME_MAX_LENGTH = 60;

const EmergencyInterventionDialog = ({ open, entry, fromEsiOne = false, onCancel, onConfirm }) => {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Reset whenever (re)opened for a fresh entry.
  useEffect(() => {
    if (open) {
      setName('');
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && !submitting;

  const handleConfirm = async () => {
    if (!canSave) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm(trimmed);
    } catch (err) {
      console.error('[SavoirCare] Failed to flag emergency intervention:', err);
      setSubmitError(err?.message || 'Failed to save. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      data-testid="savoir-care-emergency-dialog"
    >
      <DialogTitle>Flag emergency intervention</DialogTitle>
      <DialogContent>
        {fromEsiOne && (
          <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
            ESI 1 assigned. Flag this patient as an emergency intervention? If yes, enter the
            intervention name below.
          </Alert>
        )}

        {entry?.chiefComplaintSummary && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Patient: {entry.chiefComplaintSummary}
          </Typography>
        )}

        <TextField
          autoFocus
          label="Intervention name"
          placeholder='e.g. "Cardioversion"'
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          inputProps={{
            maxLength: INTERVENTION_NAME_MAX_LENGTH,
            'data-testid': 'savoir-care-emergency-name',
          }}
          helperText={`Required. The card turns red; this name is shown on it (max ${INTERVENTION_NAME_MAX_LENGTH} characters).`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) handleConfirm();
          }}
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          {fromEsiOne ? 'Not now' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={!canSave}
          data-testid="savoir-care-emergency-save"
        >
          {submitting ? 'Saving…' : 'Flag intervention'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmergencyInterventionDialog;
